<!-- frontend/src/components/EmployeeLocationNotification.vue -->
<template>
  <!-- Critical Location Required Modal (Cannot be dismissed by employees) -->
  <div
    v-if="showLocationRequired && authStore.isEmployee"
    class="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
      <div class="flex items-center mb-4">
        <div class="flex-shrink-0">
          <AlertTriangle class="h-8 w-8 text-red-600" />
        </div>
        <div class="ml-3">
          <h3 class="text-lg font-medium text-gray-900">
            Location Access Required
          </h3>
        </div>
      </div>

      <div class="mb-6">
        <p class="text-sm text-gray-700 mb-4">
          Location tracking is mandatory for all employees. This is required
          for:
        </p>

        <ul class="text-sm text-gray-600 space-y-2 mb-4">
          <li class="flex items-center">
            <Shield class="w-4 h-4 text-green-500 mr-2" />
            <span>Security and safety monitoring</span>
          </li>
          <li class="flex items-center">
            <Clock class="w-4 h-4 text-blue-500 mr-2" />
            <span>Accurate time and attendance</span>
          </li>
          <li class="flex items-center">
            <MapPin class="w-4 h-4 text-purple-500 mr-2" />
            <span>Field work verification</span>
          </li>
          <li class="flex items-center">
            <Building class="w-4 h-4 text-orange-500 mr-2" />
            <span>Office and project site tracking</span>
          </li>
        </ul>

        <div class="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p class="text-sm text-red-800 font-medium">
            You must enable location access to continue working.
          </p>
        </div>

        <!-- Error message if location fails -->
        <div
          v-if="locationError"
          class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4"
        >
          <p class="text-sm text-yellow-800">
            <strong>Error:</strong> {{ locationError }}
          </p>
        </div>

        <!-- Instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p class="text-sm text-blue-800">
            <strong>How to enable:</strong>
          </p>
          <ol class="text-sm text-blue-700 mt-2 space-y-1 ml-4">
            <li>1. Click "Enable Location Access" below</li>
            <li>2. Click "Allow" when your browser asks for permission</li>
            <li>3. If blocked, check your browser's location settings</li>
          </ol>
        </div>
      </div>

      <div class="flex flex-col space-y-3">
        <button
          @click="requestLocationAccess"
          :disabled="requesting"
          class="w-full flex items-center justify-center px-4 py-2 bg-red-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
        >
          <MapPin v-if="!requesting" class="w-4 h-4 mr-2" />
          <div
            v-else
            class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
          ></div>
          {{ requesting ? 'Requesting Access...' : 'Enable Location Access' }}
        </button>

        <!-- Help options -->
        <div class="border-t pt-3">
          <p class="text-xs text-gray-500 text-center mb-2">
            Need help? Contact:
          </p>
          <div class="flex justify-center space-x-4 text-xs">
            <a
              href="mailto:it@company.com"
              class="text-blue-600 hover:text-blue-800"
            >
              IT Support
            </a>
            <span class="text-gray-300">|</span>
            <a href="tel:+1234567890" class="text-blue-600 hover:text-blue-800">
              Call Support
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Less intrusive notification for ongoing issues -->
  <div
    v-else-if="locationError && authStore.isEmployee && !showLocationRequired"
    class="fixed top-4 right-4 max-w-sm bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-40"
  >
    <div class="flex items-center">
      <AlertTriangle class="w-5 h-5 mr-2" />
      <div>
        <p class="font-bold text-sm">Location Required</p>
        <p class="text-xs">{{ locationError }}</p>
        <button
          @click="showLocationRequired = true"
          class="text-xs underline hover:no-underline mt-1"
        >
          Fix Now
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import {
  AlertTriangle,
  Shield,
  Clock,
  MapPin,
  Building,
} from 'lucide-vue-next';

// Store
const authStore = useAuthStore();

// State
const requesting = ref(false);
const showLocationRequired = ref(false);

// Computed
const locationError = computed(() => authStore.locationStatus?.error);
const isLocationMandatory = computed(() => authStore.isLocationMandatory);

// Methods
const requestLocationAccess = async () => {
  if (requesting.value) return;

  requesting.value = true;
  try {
    console.log('🎯 Employee requesting mandatory location access...');

    const success = await authStore.forceMandatoryLocationTracking();

    if (success) {
      showLocationRequired.value = false;
      console.log('✅ Employee location access granted');
    } else {
      console.error('❌ Failed to enable location access');
    }
  } catch (error) {
    console.error('Location access error:', error);
  } finally {
    requesting.value = false;
  }
};

// Watch for critical location failures
watch(
  () => authStore.showLocationRequired,
  required => {
    if (required && authStore.isEmployee) {
      showLocationRequired.value = true;
    }
  },
  { immediate: true }
);

// Watch for location errors
watch(
  () => authStore.locationStatus?.error,
  error => {
    if (error && authStore.isEmployee && isLocationMandatory.value) {
      // Show modal for critical errors, notification for minor ones
      const isCritical =
        error.includes('required') ||
        error.includes('REQUIRED') ||
        error.includes('mandatory');
      if (isCritical) {
        showLocationRequired.value = true;
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped>
/* Loading animation */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Modal cannot be dismissed */
.fixed.inset-0 {
  pointer-events: all;
}

/* Ensure modal stays on top */
.z-50 {
  z-index: 9999;
}
</style>
